






















import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import {
  MapboxOptions,
  Style,
  EaseToOptions,
  LngLatBounds,
  FitBoundsOptions,
  Map,
  CameraForBoundsResult,
} from 'mapbox-gl';
import { NXWConfig, Building, CameraLevel } from '@/types';
import AnimatedMapboxGraphic from '@/lib/map/AnimatedMapboxGraphic';
import MapBuilding from '@/components/organisms/MapBuilding.vue';

// We have moved the animationFrame number and sprites array to the window
// object to ensure that they are non-reactive in Vue
declare global {
  interface Window {
    animationFrame: number | undefined | null;
    sprites: AnimatedMapboxGraphic[];
    then: number;
  }
}

@Component({
  components: {
    MapBuilding,
    MapDebug: () => import('@/components/molecules/MapDebug.vue'),
  },
})
export default class Mapbox extends Vue {
  private config: NXWConfig = this.$store.state.config;

  private mapStyle: Style = {
    version: 8,
    sources: {
      base: {
        type: 'raster',
        tiles: [this.config.map.tileBackgroundUrl],
        tileSize: this.config.map.tileSize,
        minzoom: 0,
        maxzoom: 1,
      },
      background: {
        type: 'raster',
        tiles: [this.config.map.tileBackgroundUrl],
        tileSize: this.config.map.tileSize,
        minzoom: 0,
        maxzoom: 6,
      },
      // Conditionally display the foreground tiles or an empty geojson collection (To keep the "foreground" layer in place for sprite adding)
      ...(this.$store.state.isHighFidelity
        ? {
            foreground: {
              type: 'raster',
              tiles: [this.config.map.tileForegroundUrl],
              tileSize: this.config.map.tileSize,
              minzoom: 0,
              maxzoom: 6,
              bounds: [-57, -18, 47, 29],
            },
          }
        : {
            foreground: {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: [],
              },
            },
          }),
    },
    layers: [
      {
        id: 'base',
        type: 'raster',
        source: 'base',
        paint: {
          'raster-fade-duration': 0,
        },
      },
      {
        id: 'background',
        type: 'raster',
        source: 'background',
        minzoom: 0,
        maxzoom: 6,
        paint: {
          'raster-fade-duration': 0,
        },
      },
      // Conditionally display the foreground tiles or an empty geojson collection (To keep the "foreground" layer in place for sprite adding)
      ...(this.$store.state.isHighFidelity
        ? [
            {
              id: 'foreground',
              type: 'raster',
              source: 'foreground',
              minzoom: 0,
              maxzoom: 6,
              paint: {
                'raster-fade-duration': 0,
              },
            } as mapboxgl.Layer,
          ]
        : [
            {
              id: 'foreground',
              type: 'symbol',
              source: 'foreground',
            } as mapboxgl.Layer,
          ]),
    ],
  };

  private mapOptions: MapboxOptions = {
    container: '',
    center: [0, 0],
    zoom: this.config.map.cameraLevels[CameraLevel.MINIMUM].zoom,
    boxZoom: false,
    doubleClickZoom: false,
    dragRotate: false,
    dragPan: false,
    scrollZoom: false,
    pitchWithRotate: false,
    touchPitch: false,
    style: this.mapStyle,
    maxBounds: this.config.map.initialBoundaries,
    minZoom: this.config.map.cameraLevels[CameraLevel.MINIMUM].zoom,
    maxZoom: this.config.map.cameraLevels[CameraLevel.MAXIMUM].zoom,
    touchZoomRotate: false,
    renderWorldCopies: false,
    keyboard: false,
  };

  private map!: Map;

  private mapLoaded = false;

  private zoomLevel = 0;

  private buildings: Building[] = [];

  @Prop() inactive!: boolean;

  private fps = this.config.animationFps || 30;

  private fpsInterval = 1000 / this.fps;

  @Watch('inactive') private onActiveChanged(newVal: boolean, oldVal: boolean) {
    if (oldVal === true && newVal === false) {
      this.startAnimations();
    } else {
      this.stopAnimations();
    }
  }

  private mounted() {
    this.mapOptions.container = this.$refs.mapContainer as HTMLElement;
    this.map = new Map(this.mapOptions);
    this.map.once('load', () => this.onLoad());
    this.map.on('zoom', this.onZoom);
    this.updateZoomLevel();
    window.then = window.performance.now();
  }

  private onLoad() {
    this.mapLoaded = true;
    this.$emit('map-loaded');
    this.watchPageVisibility();
    // Wait two ticks, if we need to add MapDebug component this must be added before the sprites are added
    // A weird bug, but if we add the MapDebug MapboxDraw afterwards it fails to create the layers/sources
    // And ends up a useless debug tool that doesnt let you create any routes for the spritesheets.
    setTimeout(
      () => {
        this.addSprites();
      },
      this.config.debug ? 1000 : 0
    );
  }

  private onZoom() {
    this.updateZoomLevel();
  }

  private updateZoomLevel() {
    this.zoomLevel = this.map.getZoom();
  }

  private watchPageVisibility() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onVisibilityChange); // Safari
  }

  private onVisibilityChange() {
    if (!document.hidden && !this.inactive) {
      this.startAnimations();
    } else {
      this.stopAnimations();
    }
  }

  private addSprites() {
    if (!window.sprites) window.sprites = [];
    const spritesLoadedArray: Promise<void>[] = [];
    const isHighFidelity = this.$store.state.isHighFidelity as boolean;
    this.config.spritesheets.forEach((spritesheet) => {
      spritesLoadedArray.push(
        new Promise((resolve, reject) => {
          if (isHighFidelity || (!isHighFidelity && spritesheet.essential)) {
            const spritesheetImage = new Image();
            spritesheetImage.src = spritesheet.url;
            spritesheetImage.onerror = () => {
              reject();
            };
            spritesheetImage.onload = () => {
              // Store image in canvas for performance
              const spriteCanvas = document.createElement('canvas');
              spriteCanvas.width = spritesheet.width;
              spriteCanvas.height = spritesheet.height;
              (spriteCanvas.getContext(
                '2d'
              ) as CanvasRenderingContext2D).drawImage(
                spritesheetImage,
                0,
                0,
                spritesheet.width,
                spritesheet.height
              );
              spritesheetImage.src = '';
              spritesheet.sprites.forEach((sprite) => {
                sprite.instances.forEach((instance) => {
                  window.sprites.push(
                    new AnimatedMapboxGraphic(
                      {
                        id: instance.id,
                        center: instance.center,
                        width: instance.width,
                        height: instance.height,
                        sequence: instance.sequence,
                        route: instance.route,
                        before: instance.before,
                      },
                      {
                        image: spriteCanvas,
                        width: sprite.width,
                        height: sprite.height,
                        frameSequences: sprite.frameSequences,
                        framesPerFrame: sprite.framesPerFrame,
                      },
                      this.map,
                      isHighFidelity
                    )
                  );
                });
              });
              resolve();
            };
          } else {
            resolve();
          }
        })
      );
    });
    Promise.all(spritesLoadedArray).finally(() => {
      if (!this.inactive) this.startAnimations();
      this.$emit('sprites-loaded');
    });
  }

  private startAnimations() {
    if (window.animationFrame) return;
    window.animationFrame = window.requestAnimationFrame(this.stepAnimations);
    window.sprites.forEach((sprite) => sprite.playCanvasSource());
  }

  // eslint-disable-next-line class-methods-use-this
  private stopAnimations() {
    if (!window.animationFrame) return;
    window.cancelAnimationFrame(window.animationFrame);
    window.animationFrame = null;
    window.sprites.forEach((sprite) => sprite.pauseCanvasSource());
  }

  private stepAnimations(newTime: number) {
    // Request another frame
    window.animationFrame = window.requestAnimationFrame(this.stepAnimations);
    // Throttle animations to configured FPS
    const elapsed = newTime - window.then;
    if (elapsed <= this.fpsInterval) return;
    window.then = newTime - (elapsed % this.fpsInterval);
    // Decide what sprites are onscreen to animate/move
    const bounds = this.map.getBounds();
    window.sprites.forEach((sprite) => {
      if (sprite.hasRoute()) sprite.stepPosition();
      if (sprite.isAnimated() || sprite.hasRoute()) {
        const isInBounds = sprite
          .getCoordinates()
          .some((coordinates) =>
            bounds.contains(coordinates as [number, number])
          );
        if (isInBounds) sprite.stepAnimation();
      }
    });
  }

  private displayBuildingMarkers() {
    this.buildings = this.$store.getters[
      'buildings/getConditionalBuildings'
    ]().sort(
      (a: Building, b: Building) => b.markerPosition[1] - a.markerPosition[1]
    ); // Sort by latitude so markers further south are added on top
  }

  private onBuildingPressed(building: Building) {
    this.$emit('building-pressed', building);
  }

  public doMapFlyInTransition(animated: boolean): Promise<void> {
    return new Promise((resolve) => {
      this.map.once('moveend', () => {
        resolve();
      });
      if (animated) {
        this.map.flyTo({
          zoom: this.config.map.cameraLevels[CameraLevel.NORMAL].zoom,
          center: this.config.map.center,
          speed: 0.25,
        });
      } else {
        this.map.jumpTo({
          zoom: this.config.map.cameraLevels[CameraLevel.NORMAL].zoom,
          center: this.config.map.center,
        });
      }
    });
  }

  public startMapContent() {
    this.displayBuildingMarkers();
    this.map.setMaxBounds(this.config.map.zoomedBoundaries);
    this.unlockControls();
  }

  public enterLocation(bounds: LngLatBounds, animated: boolean): Promise<void> {
    const cfb: CameraForBoundsResult = this.map.cameraForBounds(bounds, {
      padding: {
        top: 0,
        right: 0,
        bottom: this.map.getContainer().clientHeight / 2,
        left: 0,
      },
    }) as CameraForBoundsResult;
    return new Promise((resolve) => {
      this.lockControls();
      if (animated) {
        this.map.easeTo({
          center: cfb.center,
          zoom: cfb.zoom,
          essential: false,
        } as FitBoundsOptions);
      } else {
        this.map.jumpTo({
          center: cfb.center,
          zoom: cfb.zoom,
        });
      }
      this.map.once('moveend', () => resolve());
    });
  }

  public exitLocation(): Promise<void> {
    return new Promise((resolve) => {
      this.map.easeTo({
        zoom: this.config.map.cameraLevels[CameraLevel.NORMAL].zoom,
        essential: false,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      } as EaseToOptions);
      this.map.once('moveend', () => {
        this.unlockControls();
        resolve();
      });
    });
  }

  private lockControls() {
    this.map.dragPan.disable();
    this.map.scrollZoom.disable();
    this.map.touchZoomRotate.disable();
    this.map.doubleClickZoom.disable();
  }

  private unlockControls() {
    this.map.dragPan.enable();
    this.map.scrollZoom.enable();
    this.map.touchZoomRotate.enable();
    this.map.touchZoomRotate.disableRotation();
    this.map.doubleClickZoom.enable();
  }
}
